import { Box, useScaleFactor } from "arkh";
import React, { useState, useRef, useCallback, useMemo } from "react";
import {
  Animated,
  Modal,
  Pressable,
  useWindowDimensions,
  View,
} from "react-native";

export function PopoverButton(props: {
  children: (open: () => void) => React.ReactNode;
  popoverContent: (close: () => void) => React.ReactNode;
  popoverWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0, w: 10 });
  const ref = useRef<View>(null);

  const onOpen = useCallback(() => {
    ref.current?.measure((x, y, width, height, pageX, pageY) => {
      setPos({ x: pageX, y: pageY + height, w: width });
      setOpen(true);
    });
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <View ref={ref} collapsable={false}>
      <Box spaceLeft="medium" />
      {props.children(onOpen)}
      {open ? (
        <AbsolutePopover
          top={pos.y ?? 0}
          left={pos.x ?? 0}
          buttonWidth={pos.w}
          onClose={onClose}
        >
          {props.popoverContent(onClose)}
        </AbsolutePopover>
      ) : null}
    </View>
  );
}

function AbsolutePopover(props: {
  children: React.ReactNode;
  top: number;
  left: number;
  buttonWidth: number;
  onClose: () => void;
}) {
  const dimensions = useWindowDimensions();
  const side = useMemo(
    () =>
      props.left + props.buttonWidth / 2 > dimensions.width / 2
        ? "left"
        : "right",
    [props.left, props.buttonWidth, dimensions.width]
  );
  const verticalPosition = useMemo(
    () => (props.top < dimensions.height / 2 ? "top" : "bottom"),
    [props.top, dimensions.height]
  );

  const sf = useScaleFactor();
  const popoverWidth = 350 * sf;

  return (
    <Modal
      visible
      transparent
      presentationStyle="overFullScreen"
      animationType={"none"}
      onRequestClose={props.onClose}
    >
      <View
        style={{
          position: "absolute",
          height: dimensions.height,
          width: dimensions.width,
        }}
      >
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            zIndex: 1000,
            width: "100%",
            height: "100%",
            justifyContent: "flex-start",
          }}
        >
          <Animated.View
            style={[
              {
                width: "100%",
                height: "100%",
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                position: "absolute",
                backgroundColor: "rgba(0,0,0,0.1)",

                zIndex: -1,
              },
            ]}
          >
            <Pressable
              onPress={() => {
                props.onClose();
              }}
              style={{
                flex: 1,
              }}
            />
          </Animated.View>
          <Animated.View
            style={{
              position: "absolute",
              maxHeight:
                dimensions.height -
                (verticalPosition === "top"
                  ? props.top
                  : dimensions.height - props.top),

              width: popoverWidth,
              top: verticalPosition === "top" ? props.top : undefined,
              bottom:
                verticalPosition === "bottom"
                  ? dimensions.height - props.top
                  : undefined,
              left:
                props.left -
                (side === "left" ? popoverWidth - props.buttonWidth : 0),
            }}
          >
            <Box
              style={{
                overflow: "hidden",
                flex: 1,
              }}
              cornerRadius="large"
              color="surface-primary"
            >
              {props.children}
            </Box>
          </Animated.View>
        </View>
      </View>
    </Modal>
  );
}
